import {
  API_BASE_URL,
  parseIstatecResponse,
  parseValidationError,
  type IstatecResponse,
} from './apiConfig';

export interface ApplyForRolePayload {
  name: string;
  personal_email: string;
  work_experience: string;
  role: string;
  current_company: string;
  phone_number: string;
  message: string;
  otp: string;
  attachment: File;
}

export const ALLOWED_RESUME_EXTENSIONS = [
  'pdf',
  'doc',
  'docx',
  'txt',
  'rtf',
  'odt',
  'jpg',
  'jpeg',
  'png',
] as const;

export const MAX_RESUME_BYTES = 10 * 1024 * 1024; // 10 MB

export function getFileExtension(file: File): string {
  const parts = file.name.split('.');
  if (parts.length < 2) return '';
  return parts[parts.length - 1].toLowerCase();
}

export function validateResume(file: File): { ok: true } | { ok: false; message: string } {
  if (!file || file.size === 0) {
    return { ok: false, message: 'Attachment is empty.' };
  }
  if (file.size > MAX_RESUME_BYTES) {
    const mb = (file.size / (1024 * 1024)).toFixed(1);
    return { ok: false, message: `Attachment is ${mb} MB — max allowed is 10 MB.` };
  }
  const ext = getFileExtension(file);
  if (!ALLOWED_RESUME_EXTENSIONS.includes(ext as (typeof ALLOWED_RESUME_EXTENSIONS)[number])) {
    return {
      ok: false,
      message: `Unsupported file type ".${ext}". Allowed: ${ALLOWED_RESUME_EXTENSIONS.join(', ')}.`,
    };
  }
  return { ok: true };
}

/**
 * POST /apply_for_role — multipart/form-data.
 * OTP is verified against `personal_email` (issued via /send_otp_email_validation,
 * `require_work_email` defaulted to false for this flow).
 */
export async function applyForRole(payload: ApplyForRolePayload): Promise<IstatecResponse> {
  try {
    const form = new FormData();
    form.append('name', payload.name);
    form.append('personal_email', payload.personal_email);
    form.append('work_experience', payload.work_experience);
    form.append('role', payload.role);
    form.append('current_company', payload.current_company);
    form.append('phone_number', payload.phone_number);
    form.append('message', payload.message);
    form.append('otp', payload.otp);
    form.append('attachment', payload.attachment, payload.attachment.name);

    const res = await fetch(`${API_BASE_URL}/apply_for_role`, {
      method: 'POST',
      body: form,
    });
    if (res.status === 422) return parseValidationError(res);
    return parseIstatecResponse(res);
  } catch {
    return {
      status: 'error',
      message: 'Network error. Please check your connection and try again.',
      status_code: 0,
      data: null,
      request_id: '',
    };
  }
}
